/**
 * The announcement video.
 *
 * ── WHAT ACTUALLY GATES THIS, WHICH IS NOT WHAT THE PROP NAME SUGGESTS ──────
 *
 * PinnedAnnouncement's `embedUrl` prop was documented as "signed, expiring
 * embed". It is not, and it cannot be: private Vimeo does not mint signed
 * per-viewer URLs on the Starter tier. What B01 actually bought is two
 * independent gates, and it is worth being exact about them because the
 * difference decides what a leaked URL is worth.
 *
 *   1. THE HUB LOGIN. This module is only imported by a route that renders
 *      behind authentication, so the page carrying the embed is not reachable
 *      signed-out. This is the gate that matters and it is ours.
 *
 *   2. VIMEO DOMAIN-LEVEL PRIVACY. The video is set to play only when embedded
 *      on an allow-listed domain. Vimeo enforces that by checking the Referer
 *      header on the embed request. Paste the URL into a browser or an unlisted
 *      site and it refuses.
 *
 * So the URL below is not a secret — it ships in a public JS bundle and anyone
 * signed in can read it out of devtools. It is not supposed to be a secret. The
 * protection is that it only plays on our host, and the reason there is no
 * "share" affordance anywhere near it is that this is internal until the
 * external launch in April 2027.
 *
 * ── THE FAILURE MODE, AND WHY netlify.toml LOOKS LIKE IT IS ABOUT PRIVACY ───
 *
 * A referrer check needs a referrer. `Referrer-Policy: no-referrer` — which is
 * the tempting, apparently-safer setting, and which engagement-hub-v2 shipped
 * once — makes the embed fail on EVERY host including the allow-listed one, and
 * it fails looking exactly like the video was deleted. The platform's
 * netlify.toml sets `strict-origin-when-cross-origin`, which sends the bare
 * origin and no path. Do not "tighten" it.
 *
 * The iframe also carries an explicit referrerPolicy attribute, because in
 * local dev there is no Netlify and therefore no header, and a video that only
 * works in production is a video nobody tests.
 *
 * ── OPERATIONAL: THE ALLOW-LIST IS A MANUAL STEP ON vimeo.com ───────────────
 *
 * Every host that must play this needs adding under the video's Privacy →
 * "Where can this be embedded?" list. Checked 30 Aug 2026 and now holding
 * oneconetec.com, oneconetec.netlify.app and localhost. Nothing in this repo
 * can change it, and if a host is missing the player shows "Because of its
 * privacy settings, this video cannot be played here." That message is the
 * allow-list, not a bug in this file. OPS-21 is the issue that proves it plays
 * on a host that actually serves the app.
 *
 * ── WHY THE VIDEO IS NOT A CONTENT ROW ──────────────────────────────────────
 *
 * There is exactly one announcement video and the content model has no
 * attachment abstraction yet — that is FND-6, unbuilt. Rather than invent a
 * media column on `content` to carry a single known artifact, the pinned slot
 * knows about this one video and the feed does not carry media at all.
 *
 * Migration 015 gives `promotion` a video_provider/video_ref pair for the hero
 * slot, so a PROMOTED video is already data rather than a constant. This module
 * remains the fallback for the one video that predates that, and it is what
 * VideoPlayer falls back to when no hero promotion is live. When FND-6 lands,
 * both this constant and those two columns move to an attachment row.
 */

/**
 * Private Vimeo id. Not a secret — see the note above.
 *
 * 1234736225 is the FINAL cut of "Welcome to OneConetec" (David, 10 Oct),
 * replacing 1233202572, the 5 Oct cut held off the hub since 6 Oct. Its embed
 * allow-list is set by hand on vimeo.com like every new id. History below.
 *
 * 1233202572 is "Welcome to OneConetec", which REPLACED the 40-years video
 * (1222038815) on 5 Oct 2026, David's call, for the 7/13 Oct launch. A new id
 * is a new video on vimeo.com, so its embed allow-list (the OPERATIONAL note
 * above) is set again by hand — it does not carry over from the old one.
 *
 * It is the SECOND upload of that video. The first, 1233117131, stalled at
 * "Upload pending" with no file for five hours and could not be replaced, so
 * David uploaded again. Do not point back at 1233117131: the player answers
 * "This video does not exist", which reads exactly like a privacy refusal.
 */
const VIMEO_ID = '1234736225'

/**
 * Player parameters, each one chosen:
 *   dnt=1        no Vimeo analytics cookies. Staff did not consent to Vimeo
 *                tracking to watch an internal announcement, and this keeps the
 *                embed out of the cookie-consent conversation entirely.
 *   title,byline,portrait=0
 *                strips the Vimeo chrome that links back to the public video
 *                page — which is a route out of the hub to a URL we do not
 *                want circulating.
 *   badge=0      removes the Vimeo badge, same reason.
 *   autopause=0  do not pause this because another Vimeo embed elsewhere
 *                started; there is no other embed, and the behaviour is
 *                confusing when it fires.
 *   controls=0   WE draw the controls. See VideoPlayer.tsx: Vimeo's own chrome
 *                is a fixed size that no embed parameter enlarges, and its
 *                buttons are far below a 44px touch target. A crew member in
 *                gloves on a tablet cannot reliably hit them.
 *   playsinline=1
 *                iOS Safari otherwise takes the video fullscreen the instant it
 *                plays, which throws the viewer out of the hub. Without this,
 *                muted autoplay does not work on iPhone at all.
 *   muted=1      Not a preference — a requirement. Every current browser blocks
 *                autoplay with sound, and blocks it SILENTLY: the play() promise
 *                rejects and the viewer sees a frozen first frame that looks
 *                like a broken video. Muted autoplay plus a loud unmute control
 *                is the only shape of "auto-start" that actually exists.
 */
const PLAYER_PARAMS = [
  'badge=0',
  'autopause=0',
  'dnt=1',
  'title=0',
  'byline=0',
  'portrait=0',
  'controls=0',
  'playsinline=1',
  'muted=1',
].join('&')

/**
 * ── THE URL PARAMS ARE NOT ENOUGH, AND THAT COST US A DOUBLED CONTROL BAR ────
 *
 * Fixed 31 Aug. Vimeo's own chrome was visible INSIDE our control bar — two
 * players' worth of buttons stacked on one video.
 *
 * The cause: `new Player(el, { url })` does not embed that URL. The SDK parses
 * it for the video id and then builds a fresh iframe from its OPTIONS OBJECT,
 * so every query parameter above is discarded — `controls=0` included. The URL
 * looked authoritative, and was decoration.
 *
 * So the same settings are ALSO expressed as SDK options below, and those are
 * what actually apply. Both exist because the URL form is still needed for the
 * `title` attribute and for anything that embeds an iframe directly without the
 * SDK, and having them disagree is how this happened. **Change them together.**
 *
 * `controls: false` is the load-bearing one — see VideoPlayer.tsx for why we
 * draw our own — but every option here is the same decision as its parameter
 * twin, so the comments above apply unchanged.
 */
export const PLAYER_OPTIONS = {
  controls: false,
  muted: true,
  playsinline: true,
  dnt: true,
  title: false,
  byline: false,
  portrait: false,
  autopause: false,
  // The SDK also draws a "watch on Vimeo" affordance on some versions unless
  // told not to. There is no path out of the hub to a URL we do not want
  // circulating — B01, internal until April 2027.
  transparent: false,
} as const

/**
 * ── HELD UNTIL THE EMPLOYEE LAUNCH (David, 6 Oct) ───────────────────────────
 *
 * The video is off the hub for now and comes back around 13 Oct, just before
 * everyone is invited — and it will probably be EDITED first. A constant, like
 * SIGNATURE_RELEASED, because releasing it is a code change anyway: an edited
 * video may come back from Vimeo under a new id, and VIMEO_ID above changes
 * with it. Releasing = set this true (and the id if it moved), and flip the
 * suite's assertion, which pins the value so it cannot change by accident.
 *
 * While false the hub renders NO video card at all — not a locked placeholder,
 * which would say "plays for signed-in staff only" to a signed-in person
 * (SHL-8's trap). Two consequences, both stated rather than discovered:
 *   - A pinned post is withheld with it, because the card IS the pinned slot.
 *     Prod has none and nothing can pin one, so today this hides nothing else.
 *   - Notice B (13 Oct, everybody) carries the `video` card, "Watch the
 *     announcement", which routes to #/. Release this BEFORE Notice B starts,
 *     or that card points at a hub with nothing to watch.
 *
 * RELEASED 10 Oct (David: "the new and final vimeo is up"), id 1234736225.
 */
export const VIDEO_RELEASED = true

export const ANNOUNCEMENT_VIDEO = {
  videoId: VIMEO_ID,
  embedUrl: `https://player.vimeo.com/video/${VIMEO_ID}?${PLAYER_PARAMS}`,
  /**
   * Shown as the iframe title, so it is what a screen reader announces — and,
   * since 5 Oct, the card's heading when no post is pinned (Home.tsx). It is
   * the video's name, so it is not translated.
   */
  title: 'Welcome to OneConetec',
  /**
   * No hand-typed duration. The 40-years video carried '2:55' here and it
   * would have been wrong the moment the id changed; VideoPlayer now asks the
   * SDK for the real length as soon as the embed is ready, and shows –:–– only
   * until then.
   */
} as const

/** Build the same URL for any Vimeo id, so a promoted hero video can use it. */
export function vimeoEmbedUrl(videoRef: string): string {
  return `https://player.vimeo.com/video/${videoRef}?${PLAYER_PARAMS}`
}

/**
 * The numeric id out of a player URL, for handing to the SDK as `id`.
 *
 * Passing `id` plus PLAYER_OPTIONS is the combination that works; passing `url`
 * makes the SDK ignore the options half of the URL. Returns null rather than
 * throwing, and VideoPlayer falls back to `url` in that case — a video with
 * Vimeo's small controls is a worse player, but a player.
 */
export function vimeoIdFromUrl(embedUrl: string): string | null {
  const m = /\/video\/(\d+)/.exec(embedUrl)
  return m ? m[1] : null
}

/**
 * ── SHOULD THIS AUTO-START? ─────────────────────────────────────────────────
 *
 * Decided 30 Aug 2026. Autoplay is on, muted, with a large unmute control —
 * and suppressed on the connections and for the people where starting a video
 * unasked is a cost rather than a feature.
 *
 * This overrides an earlier decision recorded in this file ("deliberately
 * absent: autoplay"), and the reasoning behind that decision has NOT gone away:
 * roughly 40% of staff hold no Microsoft licence, a good number of those are on
 * phones tethered on site, and a video that starts itself on a metered
 * connection is a bill. What changed is that the browser now tells us, so we
 * can have the motion without sending the bill.
 *
 * Three suppressions, all of them the browser's own signal rather than a guess:
 *
 *   saveData          the viewer has explicitly asked their browser to use less
 *                     data. Ignoring that is not a trade-off, it is rudeness.
 *   effectiveType     2g / slow-2g / 3g. A video that autoplays here does not
 *                     play, it buffers, and the viewer watches a spinner.
 *   prefers-reduced-motion
 *                     an accessibility setting, not a bandwidth one. Motion
 *                     starting without input can trigger vestibular symptoms.
 *                     OPS-4 is the accessibility pass; this is not optional
 *                     under WCAG 2.2 SC 2.2.2 either way.
 *
 * Everything is feature-detected. The Network Information API does not exist in
 * Safari or Firefox, and a missing signal means "no reason to suppress" — the
 * viewer on an unknown connection gets the same behaviour as the viewer on
 * wifi, because the alternative is suppressing autoplay for every Safari user
 * on the strength of an absent API.
 */
export function shouldAutoplay(): boolean {
  if (typeof window === 'undefined') return false

  try {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return false
  } catch {
    /* matchMedia is absent in some embedded webviews. Not a reason to suppress. */
  }

  const conn = (
    navigator as Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string }
    }
  ).connection

  if (conn?.saveData) return false
  if (conn?.effectiveType && /^(slow-)?2g$|^3g$/.test(conn.effectiveType)) return false

  return true
}
